import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";

import ShellCard from "components/ShellCard";

import { fmtInt } from "../../ConferenciaFatura/formatters";
import { GRANULARIDADES, fmtPeriodo, fmtPeriodoCurto } from "./formatos";
import renderEstadoSecao from "./EstadoSecao";

const EYEBROW = "Fluxo";
const TITULO = "Volume por período";
const DESCRICAO =
  "Quantas faturas entram, são liberadas, integram, travam e são contestadas em cada período. Cada série usa a data do próprio evento — o filtro de data da coorte não se aplica aqui.";

// As cinco séries. O token de cor é escolhido para os dois temas: no escuro,
// --everlog-status-warning (salmão) e --everlog-status-danger (rosa) ficam quase
// iguais lado a lado, então `warning` fica FORA e o quinto tom vem de
// --everlog-status-accent (roxo), que separa nos dois temas.
const SERIES = [
  { key: "entradas", label: "Entradas", token: "--everlog-status-info", fallback: "#1d8cf8" }, // theme-ok: fallback do canvas, cor real vem do token
  { key: "liberadas", label: "Liberadas", token: "--everlog-status-success", fallback: "#00bf9a" }, // theme-ok: fallback do canvas, cor real vem do token
  { key: "integradas", label: "Integradas", token: "--everlog-status-accent", fallback: "#a78bfa" }, // theme-ok: fallback do canvas, cor real vem do token
  { key: "bloqueadas", label: "Bloqueadas", token: "--everlog-status-danger", fallback: "#fd5d93" }, // theme-ok: fallback do canvas, cor real vem do token
  { key: "contestadas", label: "Contestadas", token: "--everlog-status-neutral", fallback: "#9a9a9a" }, // theme-ok: fallback do canvas, cor real vem do token
];

// O Chart.js desenha em canvas e só aceita cor como string — não dá para passar
// `var(--token)`. Lemos o valor computado do tema, como o mapa do Tracking
// (views/Public/Tracking/components/MapaRotaEstimada).
//
// A leitura é no <body>, NÃO no <html>: o tema claro do Hub redefine os tokens
// em `body.white-content` (_global.scss), então getComputedStyle(<html>) devolve
// sempre a paleta escura e o gráfico ficaria com as cores erradas no tema claro.
function corDoTema(token, fallback) {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const valor = window.getComputedStyle(document.body).getPropertyValue(token);
  return (valor && valor.trim()) || fallback;
}

/**
 * O tema claro/escuro do Hub é a classe `white-content` no <body>. O canvas não
 * reage a CSS, então observamos a troca de classe para recalcular as cores e
 * redesenhar o gráfico junto com o resto da página.
 */
function useTemaVersao() {
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (typeof MutationObserver === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    const observador = new MutationObserver(() => setVersao((v) => v + 1));
    observador.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observador.disconnect();
  }, []);

  return versao;
}

/**
 * Seção da H3 (PO1-3001): série temporal do fluxo, com toggle de granularidade.
 * A troca de granularidade recarrega só esta seção — o resto da tela fica.
 */
export default function VolumePeriodo({ estado, granularidade, onGranularidade }) {
  const temaVersao = useTemaVersao();
  const serie = (estado && estado.data && estado.data.serie) || [];
  const granularidadeAtiva =
    (estado && estado.data && estado.data.granularidade) || granularidade;

  const cores = useMemo(
    () => ({
      series: SERIES.map((s) => corDoTema(s.token, s.fallback)),
      texto: corDoTema("--everlog-card-muted", "#8ea7d7"), // theme-ok: fallback do canvas, cor real vem do token
      grade: corDoTema("--everlog-card-divider", "rgba(255,255,255,0.05)"), // theme-ok: fallback do canvas, cor real vem do token
    }),
    // `temaVersao` não é lido dentro do memo: ele existe só para INVALIDAR o
    // cache quando o usuário troca claro/escuro — as cores vêm de
    // getComputedStyle, que o React não tem como observar sozinho.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [temaVersao]
  );

  const dados = useMemo(() => {
    if (!serie.length) return null;
    return {
      labels: serie.map((p) => fmtPeriodoCurto(p.periodo, granularidadeAtiva)),
      datasets: SERIES.map((s, i) => ({
        label: s.label,
        data: serie.map((p) => Number(p[s.key]) || 0),
        borderColor: cores.series[i],
        backgroundColor: cores.series[i],
        pointBackgroundColor: cores.series[i],
        pointBorderColor: cores.series[i],
        pointRadius: serie.length > 40 ? 0 : 2,
        pointHoverRadius: 5,
        borderWidth: 2,
        fill: false,
        lineTension: 0.25,
      })),
    };
  }, [serie, granularidadeAtiva, cores]);

  const opcoes = useMemo(
    () => ({
      maintainAspectRatio: false,
      legend: { display: false },
      tooltips: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (itens) => {
            const idx = itens && itens.length ? itens[0].index : -1;
            return idx >= 0 && serie[idx]
              ? fmtPeriodo(serie[idx].periodo, granularidadeAtiva)
              : "";
          },
          label: (item, data) =>
            `${data.datasets[item.datasetIndex].label}: ${fmtInt(item.yLabel)}`,
        },
      },
      scales: {
        xAxes: [
          {
            ticks: { fontColor: cores.texto, maxTicksLimit: 14, autoSkip: true },
            gridLines: { display: false },
          },
        ],
        yAxes: [
          {
            ticks: {
              fontColor: cores.texto,
              beginAtZero: true,
              precision: 0,
              callback: (v) => fmtInt(v),
            },
            gridLines: { color: cores.grade, zeroLineColor: cores.grade },
          },
        ],
      },
    }),
    [cores, serie, granularidadeAtiva]
  );

  const totais = useMemo(
    () =>
      SERIES.map((s) =>
        serie.reduce((soma, p) => soma + (Number(p[s.key]) || 0), 0)
      ),
    [serie]
  );

  const bloqueio = renderEstadoSecao(estado, "Sem movimento de faturas no período.");

  const toggle = (
    <div className="lt-quick-ranges">
      {GRANULARIDADES.map((g) => (
        <button
          key={g.key}
          type="button"
          className={`lt-quick-range${granularidade === g.key ? " is-active" : ""}`}
          disabled={Boolean(estado && estado.loading)}
          onClick={() => onGranularidade(g.key)}
        >
          {g.label}
        </button>
      ))}
    </div>
  );

  return (
    <ShellCard
      eyebrow={EYEBROW}
      title={TITULO}
      description={DESCRICAO}
      action={toggle}
    >
      {bloqueio ||
        (dados ? (
          <>
            <ul className="lt-legenda">
              {SERIES.map((s, i) => (
                <li key={s.key} className="lt-legenda__item">
                  <span
                    className="lt-dot"
                    style={{ background: `var(${s.token})` }}
                  />
                  <span className="lt-legenda__label">{s.label}</span>
                  <strong className="lt-legenda__valor">{fmtInt(totais[i])}</strong>
                </li>
              ))}
            </ul>
            <div className="lt-chart">
              <Line data={dados} options={opcoes} height={260} />
            </div>
          </>
        ) : (
          <div className="lt-state">Sem movimento de faturas no período.</div>
        ))}
    </ShellCard>
  );
}
